import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { API_URL } from '../constants/api';
import AnimatedBackground from '../components/common/AnimatedBackground';

const Background = styled.div`
  min-height: 100vh;
  background: radial-gradient(ellipse at center, #0a1a0a 0%, #040804 100%);
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  z-index: 2;
`;

const VerificationCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 102, 0.2);
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: none;
  text-align: center;

  @media (max-width: 768px) {
    padding: 30px 20px;
    border-radius: 15px;
  }
`;

const Title = styled.h1`
  color: #00ff66;
  font-size: 2.5rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.h2`
  color: #88cc88;
  font-size: 1.2rem;
  text-align: center;
  margin-bottom: 30px;
  font-weight: 500;
`;

const StatusIcon = styled.div<{ success?: boolean; error?: boolean; loading?: boolean }>`
  width: 80px;
  height: 80px;
  margin: 20px auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  border-radius: 50%;
  
  ${props => {
    if (props.success) {
      return `
        background: linear-gradient(135deg, #4caf50, #45a049);
        color: white;
        &:before { content: '✓'; }
      `;
    } else if (props.error) {
      return `
        background: linear-gradient(135deg, #f44336, #d32f2f);
        color: white;
        &:before { content: '✗'; }
      `;
    } else {
      return `
        border: 3px solid rgba(0, 255, 102, 0.3);
        border-top-color: #00ff66;
        animation: spin 1s linear infinite;
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `;
    }
  }}
`;

const StatusText = styled.p`
  color: #b0b0b0;
  font-size: 1rem;
  line-height: 1.6;
  margin: 20px 0;
  text-align: center;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #00ff66, #00cc52);
  border: none;
  color: white;
  padding: 15px 30px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 20px;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #00cc52, #009940);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 255, 102, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:active {
    transform: translateY(0);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  margin: 20px 0;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 0;
    background: linear-gradient(90deg, #00ff66, #00cc52);
    border-radius: 3px;
    animation: progress 2s ease-out forwards;
  }
  
  @keyframes progress {
    to { width: 100%; }
  }
`;

enum VerificationState {
  LOADING,
  SUCCESS,
  ERROR
}

const FieldEmailVerificationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [verificationState, setVerificationState] = useState<VerificationState>(VerificationState.LOADING);
  const [message, setMessage] = useState<string>('Verifying your field email address...');
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setVerificationState(VerificationState.ERROR);
        setMessage('No verification token provided. Please check your email link and try again.');
        return;
      }

      try {
        await axios.get(`${API_URL}/api/FieldEmailVerification/${token}`);
        setVerificationState(VerificationState.SUCCESS);
        setMessage('Your field email has been successfully verified! You can now access all tactical field features.');
        
        // Дополнительная задержка для UX перед редиректом
        setTimeout(() => {
          navigate('/login');
        }, 5000);
      } catch (error) {
        console.error('Field verification error:', error);
        
        setVerificationState(VerificationState.ERROR);
        setMessage('Invalid or expired verification token. Please try registering your field again.');
      }
    };

    // Задержка для более естественной анимации загрузки
    const timer = setTimeout(() => {
      verifyEmail();
    }, 2000);

    return () => clearTimeout(timer);
  }, [token, navigate]);


  const handleNavigate = () => {
    navigate(verificationState === VerificationState.SUCCESS ? '/login' : '/register-field');
  };

  return (
    <Background>
      <AnimatedBackground />
      <Container>
        <VerificationCard>
          <Title>Field Email Verification</Title>
          <Subtitle>Secure Field Authentication</Subtitle>
          
          <StatusIcon 
            success={verificationState === VerificationState.SUCCESS}
            error={verificationState === VerificationState.ERROR}
            loading={verificationState === VerificationState.LOADING}
          />
          
          {verificationState === VerificationState.LOADING && <ProgressBar />}
          
          <StatusText>{message}</StatusText>
          
          <Button onClick={handleNavigate}>
            {verificationState === VerificationState.SUCCESS ? 'Proceed to Login' : 'Back to Field Registration'}
          </Button>
        </VerificationCard>
      </Container>
    </Background>
  );
};

export default FieldEmailVerificationPage;